import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import { Badge, Card } from "../components/ui";
import { DataTable } from "../components/DataTable";
import { QueryError, QueryLoading } from "../components/QueryState";
import { useTeamData } from "../hooks/usePlatformData";

function statusTone(status: string) {
  return status.toUpperCase() === "ATIVO" ? "success" : "warning";
}

export function TeamPage() {
  const query = useTeamData();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("TODOS");

  const rows = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase("pt-BR");
    return (query.data ?? []).filter((member) => {
      const matchesSearch =
        !normalizedSearch ||
        [member.name, member.role, member.team, member.referral]
          .join(" ")
          .toLocaleLowerCase("pt-BR")
          .includes(normalizedSearch);
      const matchesStatus = status === "TODOS" || member.status === status;
      return matchesSearch && matchesStatus;
    });
  }, [query.data, search, status]);

  if (query.isLoading) return <QueryLoading label="Carregando equipe comercial…" />;
  if (query.error) return <QueryError error={query.error} />;

  const totalSales = (query.data ?? []).reduce((sum, member) => sum + member.sales, 0);
  const activeMembers = (query.data ?? []).filter((member) => member.status === "ATIVO").length;
  const supervisors = (query.data ?? []).filter((member) =>
    member.role.toLocaleLowerCase("pt-BR").includes("supervisor"),
  ).length;

  return (
    <div className="page-stack">
      <section className="metric-grid metric-grid-four" aria-label="Resumo da equipe comercial">
        <article className="metric-card">
          <span>Equipe cadastrada</span>
          <strong>{query.data?.length ?? 0}</strong>
          <small>Vendedores e supervisão</small>
        </article>
        <article className="metric-card">
          <span>Ativos</span>
          <strong>{activeMembers}</strong>
          <small>Prontos para operar</small>
        </article>
        <article className="metric-card">
          <span>Supervisores</span>
          <strong>{supervisors}</strong>
          <small>Gestão de equipe</small>
        </article>
        <article className="metric-card">
          <span>Vendas no mês</span>
          <strong>{totalSales}</strong>
          <small>Acompanhamento comercial</small>
        </article>
      </section>

      <Card
        title="Gestão de vendedores"
        description="Centralize equipe, carteira e links comerciais. A comissão permanece fora desta operação enquanto o financeiro não estiver habilitado."
        action={
          <Link className="button button-secondary" to="/importacoes">
            Importar vendedores
          </Link>
        }
      >
        <div className="toolbar" aria-label="Filtros de vendedores">
          <label className="search-box">
            <span className="sr-only">Buscar vendedor</span>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar por nome, equipe ou código"
            />
          </label>
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            aria-label="Filtrar por status"
          >
            <option value="TODOS">Todos os status</option>
            <option value="ATIVO">Ativos</option>
            <option value="INATIVO">Inativos</option>
          </select>
        </div>

        <DataTable
          rows={rows}
          getKey={(member) => member.id}
          columns={[
            {
              key: "seller",
              header: "Vendedor",
              render: (member) => (
                <div>
                  <strong>{member.name}</strong>
                  <div className="muted">{member.role}</div>
                </div>
              ),
            },
            { key: "team", header: "Equipe", render: (member) => member.team },
            { key: "referral", header: "Código comercial", render: (member) => member.referral },
            { key: "sales", header: "Vendas no mês", render: (member) => member.sales },
            {
              key: "status",
              header: "Acesso",
              render: (member) => <Badge tone={statusTone(member.status)}>{member.status}</Badge>,
            },
          ]}
        />
      </Card>

      <Card
        title="Próximos passos da operação"
        description="A gestão comercial pode começar sem habilitar cobranças ou repasses."
      >
        <div className="three-feature-grid">
          <div className="feature-item">
            <strong>1. Cadastre ou importe a equipe</strong>
            <p>Use a importação de vendedores para preservar código, equipe e vínculo comercial.</p>
          </div>
          <div className="feature-item">
            <strong>2. Atribua famílias e vendas</strong>
            <p>
              A carteira de cada vendedor fica visível no cadastro de clientes e no histórico
              comercial.
            </p>
          </div>
          <div className="feature-item">
            <strong>3. Libere acesso por perfil</strong>
            <p>
              Vendedores operam a própria carteira; supervisores acompanham a equipe autorizada.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
